// Image formats a url's extension can name. A url without one of these (no extension, a CDN
// path) is not judged here: the API reads the file itself.
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'avif', 'bmp', 'heic', 'heif', 'svg', 'tif', 'tiff'];
const IMAGE_FORMAT_LABELS = { jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', webp: 'WebP' };

const getUrlExtension = (url) => {
    const path = String(url || '').split(/[?#]/)[0];
    const name = path.slice(path.lastIndexOf('/') + 1);
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

// False only for an image url whose extension names a format outside `formats`.
export const isImageFormatAllowed = (url, formats) => {
    const extension = getUrlExtension(url);
    return !IMAGE_EXTENSIONS.includes(extension) || formats.includes(extension);
};

// "JPEG, PNG, WebP" for ['jpg', 'jpeg', 'png', 'webp'].
export const imageFormatsLabel = (formats) =>
    [...new Set(formats.map((format) => IMAGE_FORMAT_LABELS[format] || format.toUpperCase()))].join(', ');

// The API can switch TikTok photo posts off (TIKTOK_PHOTO_POSTS) and then says photo_posts: false
// on the TikTok connection: video only, as before photo posts. Without the field they are on,
// the API default.
const TIKTOK_VIDEO_ONLY = { max_images_length: 0, max_length: 1, image_max_size_bytes: undefined };
const TIKTOK_PHOTO_POSTS = {
    max_images_length: 35,
    max_length: 35,
    image_max_size_bytes: 20 * 1024 * 1024,
    image_formats: ['jpg', 'jpeg', 'png', 'webp'],
};
const tiktokImageLimits = (companySocials) =>
    companySocials?.find?.(e => e.type === "tiktok")?.photo_posts === false ? TIKTOK_VIDEO_ONLY : TIKTOK_PHOTO_POSTS;

// The publishing rules of each network and post type, keyed like getNetworkValidationIndex():
// usePostValidator checks posts against them and the composer reads its media limits from them.
export const getPostValidationConstants = ({ companySocials, twitterLinksAllowed }) => ({
    medium_feed: {
        connection: {
            required: true
        },
        title: {
            max_length: 100,
            required: true
        },
        description: {
            max_length: 10240000,
            required: true
        }
    },
    pinterest_feed: {
        connection: {
            required: true
        },
        title: {
            max_length: 100,
            required: true
        },
        board_id: {
            required: true
        },
        description: {
            max_length: 500
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 5,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 5,
            required: true,
            image_max_size_bytes: 32 * 1024 * 1024,
            video_max_size_bytes: 2000 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 15 * 60,
            same_ratio: true,
            allow_file_cover: true,
            allow_frame_cover: true,
            required_cover: true,
        }
    },
    twitter_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: !!companySocials?.filter(e => e.type == "twitter")?.[0]?.preferences?.verified ? 25000 : 280,
            allow_links: twitterLinksAllowed
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 4,
            max_videos_length: 1,
            max_gifs_length: 1,
            max_length: 4,
            required: false,
            image_max_size_bytes: 5 * 1024 * 1024,
            video_max_size_bytes: 512 * 1024 * 1024,
            video_min_duration_seconds: 0.5, 
            video_max_duration_seconds: 140
        }
    },
    facebook_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 60000
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 10,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 10,
            required: false,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 10 * 1024 * 1024 * 1024, // 10 GB
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 60 * 20, // 20 mins
            allow_file_cover: false,
            allow_frame_cover: false,
        }
    },
    facebook_reel: {
        connection: {
            required: true
        },
        description: {
            max_length: 60000
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 0,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 1 * 1024 * 1024 * 1024, // 1 GB
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 90, // 90 secs
            video_max_width: 1920,
            video_min_width: 540,
            allowed_aspect_ratios: [0.5, 0.6],
            allow_file_cover: false,
            allow_frame_cover: false,
        }
    },
    facebook_story: {
        connection: {
            required: true
        },
        description: {
            max_length: 0
        },
        media_urls: {
            allow_multiple_types: true,
            max_images_length: 1,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 100 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 60, // 60 secs
            video_max_width: 1920,
            video_min_width: 540,
            allowed_aspect_ratios: [0.5, 0.6],
        }
    },
    instagram_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 2200
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 10,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 10,
            required: true,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 10 * 1024 * 1024 * 1024, // 10GB
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 60 * 15, // 15 MINS
            video_max_width: 1920,
            allowed_aspect_ratios: [0.5, 1.91],
            allow_file_cover: true,
            allow_frame_cover: true,
        }
    },
    instagram_reel: {
        connection: {
            required: true
        },
        description: {
            max_length: 2200
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 0,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            image_max_size_bytes: undefined,
            video_max_size_bytes: 1024 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 15 * 60, // 15 MINS
            video_max_width: 1920,
            video_min_width: 720,
            allowed_aspect_ratios: [0.5, 1.91],
            allow_file_cover: true,
            allow_frame_cover: true,
        }
    },
    instagram_story: {
        connection: {
            required: true
        },
        description: {
            max_length: 0
        },
        media_urls: {
            allow_multiple_types: true,
            max_images_length: 1,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 100 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 60, // 60 secs
            video_max_width: 1920,
            video_min_width: 540,
            allowed_aspect_ratios: [0.5, 0.6],
            allow_file_cover: false,
            allow_frame_cover: false,
        }
    },
    tiktok_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 2200,
            required: false
        },
        // One video, or a photo post of 1 to 35 images, never both. TikTok takes JPEG and WebP
        // photos up to 20 MB; the API turns PNG (and anything over 1080p) into JPEG first.
        media_urls: {
            allow_multiple_types: false,
            ...tiktokImageLimits(companySocials),
            max_videos_length: 1,
            max_gifs_length: 0,
            required: true,
            video_max_size_bytes: 4 * 1024 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: companySocials?.filter(e => e.type == "tiktok")?.[0]?.video_duration || 10 * 60,
            allow_file_cover: false,
            allow_frame_cover: true,
        },
        privacy_level: {
            required: true
        }
    },
    linkedin_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 3000,
            required: true
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 9,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 9,
            required: false,
            image_max_size_bytes: 8 * 1024 * 1024,
            video_max_size_bytes: 200 * 1024 * 1024,
            video_min_duration_seconds: 3, 
            video_max_duration_seconds: 10 * 60
        }
    },
    youtube_feed: {
        connection: {
            required: true
        },
        title: {
            max_length: 100,
            required: true
        },
        description: {
            max_length: 5000,
            required: false
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 0,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            image_max_size_bytes: undefined,
            video_max_size_bytes: 128 * 1024 * 1024 * 1024, // 128GB
            video_min_duration_seconds: 1,
            video_max_duration_seconds: 12 * 60 * 60, // 12 hours
            video_max_width: 7680, // 8K
            video_min_width: 426, // 240p
            allowed_aspect_ratios: [0.3333, 2.3922], // between 1:3 and 21.9:9
            allow_file_cover: true,
            allow_frame_cover: true,
        }
    },
    youtube_short: {
        connection: {
            required: true
        },
        title: {
            max_length: 100,
            required: false
        },
        description: {
            max_length: 5000,
            required: false
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 0,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 1,
            required: true,
            video_max_size_bytes: 256 * 1024 * 1024, // 256MB
            video_min_duration_seconds: 1,
            video_max_duration_seconds: 60, // 60 seconds
            video_max_width: 1920,
            video_min_width: 600,
            allowed_aspect_ratios: [0.5, 0.625], // Must be 9:16 (aprox: check this)
            allow_file_cover: true,
            allow_frame_cover: true,
        }
    },
    threads_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 500
        },
        media_urls: {
            allow_multiple_types: true,
            max_images_length: 20,
            max_videos_length: 20,
            max_gifs_length: 20,
            max_length: 20,
            required: false,
            image_max_size_bytes: 5 * 1024 * 1024,
            video_max_size_bytes: 512 * 1024 * 1024,
            video_min_duration_seconds: 0.5, 
            video_max_duration_seconds: 140
        }
    },
    bluesky_feed: {
        connection: {
            required: true
        },
        description: {
            max_length: 300
        },
        media_urls: {
            allow_multiple_types: false,
            max_images_length: 4,
            max_videos_length: 1,
            max_gifs_length: 0,
            max_length: 4,
            required: false,
            image_max_size_bytes: 1 * 1024 * 1024,
            video_max_size_bytes: 50 * 1024 * 1024,
            video_min_duration_seconds: 1, 
            video_max_duration_seconds: 180
        }
    }
});
